import React from "react"
import { styled } from "@mui/material/styles"
import PropTypes from "prop-types"
import { compose } from "redux"
import { connect } from "react-redux"
import Snackbar from "@mui/material/Snackbar"
import SnackbarContent from "@mui/material/SnackbarContent"
import CloseIcon from "@mui/icons-material/Close"
import IconButton from "@mui/material/IconButton"
import { green, amber } from "@mui/material/colors"

import { clearSnackbar } from "../../actions/globalUiActions"

const PREFIX = "GlobalSnackbar"

const classes = {
  success: `${PREFIX}-success`,
  error: `${PREFIX}-error`,
  info: `${PREFIX}-info`,
  warning: `${PREFIX}-warning`,
}

const StyledSnackbar = styled(Snackbar)(({ theme }) => ({
  [`& .${classes.success}`]: {
    backgroundColor: green[600],
  },

  [`& .${classes.error}`]: {
    backgroundColor: theme.palette.error.dark,
  },

  [`& .${classes.info}`]: {
    backgroundColor: theme.palette.primary.dark,
  },

  [`& .${classes.warning}`]: {
    backgroundColor: amber[700],
  },
}))

class GlobalSnackbar extends React.PureComponent {
  // This queue seems so random.
  // See here: https://material-ui.com/demos/snackbars/
  queue = []

  constructor(props) {
    super(props)

    this.state = {
      open: false,
      messageInfo: {},
    }
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      this.props.snackbar &&
      nextProps.snackbar &&
      this.props.snackbar.timestamp !== nextProps.snackbar.timestamp &&
      nextProps.snackbar.message !== ""
    ) {
      // We got a new message, so close the old one, open the new one.
      this.queue.push(nextProps.snackbar)

      if (this.state.open) {
        this.setState({ open: false })
      } else {
        this.processQueue()
      }
    }
  }

  processQueue = () => {
    if (this.queue.length > 0) {
      this.setState({
        messageInfo: this.queue.shift(),
        open: true,
      })
    }
  }

  handleExited = () => {
    this.processQueue()
  }

  handleRequestSnackbarClose = (e, r) => {
    if (r === "clickaway") {
      return
    }
    this.setState({ open: false })
    this.props.clearSnackbar()
  }

  render() {
    const {
      open,
      messageInfo: { message, variant, key, autoHideDuration },
    } = this.state

    return (
      <StyledSnackbar
        key={key}
        open={open}
        autoHideDuration={autoHideDuration}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        onClose={this.handleRequestSnackbarClose}
        TransitionProps={{ onExited: this.handleExited }}
        data-cy="global-snackbar"
        style={{ display: open ? "block" : "none" }}
      >
        <SnackbarContent
          className={classes[variant]}
          message={message}
          action={[
            <IconButton
              key="close"
              aria-label="Close"
              color="inherit"
              onClick={this.handleRequestSnackbarClose}
              size="large"
            >
              <CloseIcon />
            </IconButton>,
          ]}
        />
      </StyledSnackbar>
    )
  }
}

GlobalSnackbar.propTypes = {
  snackbar: PropTypes.shape({
    message: PropTypes.string.isRequired,
    variant: PropTypes.oneOf(["success", "warning", "error", "info"])
      .isRequired,
    autoHideDuration: PropTypes.number,
    timestamp: PropTypes.number.isRequired,
  }).isRequired,
  clearSnackbar: PropTypes.func.isRequired,
}

const mapStateToProps = (state) => {
  const {
    globalUi: { snackbar },
  } = state

  return { snackbar }
}

export default compose(connect(mapStateToProps, { clearSnackbar }))(
  GlobalSnackbar,
)
